import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const MENTALAutomationSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const automationProcesses = [
    {
      id: 'PA',
      title: 'Prior Authorization',
      description: 'Automate prior authorization requests for mental health services. Our bot submits PA requests with clinical documentation, tracks approval status across multiple payers, and reduces treatment delays by 80%.',
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      borderColor: 'rgba(139, 92, 246, 0.3)'
    },
    {
      id: 'TD',
      title: 'Treatment Documentation',
      description: 'Automatically generate compliant session notes and treatment plans. The bot structures clinical documentation to meet insurance requirements while maintaining therapeutic integrity.',
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.3)'
    },
    {
      id: 'CF',
      title: 'Claims Filing',
      description: 'Submit mental health claims with accurate CPT and DSM coding. The bot validates claim data, attaches required documentation, and submits to appropriate payers for faster reimbursement.',
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      borderColor: 'rgba(16, 185, 129, 0.3)'
    },
    {
      id: 'RP',
      title: 'Revenue Posting',
      description: 'Automatically post insurance payments and patient responsibility amounts. The bot reconciles ERAs and EOBs, posts payments, and flags discrepancies for review.',
      color: '#f97316',
      bgColor: 'rgba(249, 115, 22, 0.1)',
      borderColor: 'rgba(249, 115, 22, 0.3)'
    },
    {
      id: 'UM',
      title: 'Utilization Management',
      description: 'Automate utilization review processes with insurance companies. The bot tracks authorized sessions, submits renewal requests, and alerts clinicians before authorization expires.',
      color: '#ec4899',
      bgColor: 'rgba(236, 72, 153, 0.1)',
      borderColor: 'rgba(236, 72, 153, 0.3)'
    },
    {
      id: 'DM',
      title: 'Denial Management',
      description: 'Intelligently handle mental health claim denials. The bot analyzes denial reasons, determines appropriate appeals, and resubmits claims with corrected documentation.',
      color: '#ef4444',
      bgColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: 'rgba(239, 68, 68, 0.3)'
    },
    {
      id: 'PP',
      title: 'Patient Payments',
      description: 'Streamline patient payment collection with automated statements, payment plans, and online payment options. Reduce outstanding balances while maintaining therapeutic relationships.',
      color: '#06b6d4',
      bgColor: 'rgba(6, 182, 212, 0.1)',
      borderColor: 'rgba(6, 182, 212, 0.3)'
    },
    {
      id: 'CR',
      title: 'Compliance Reporting',
      description: 'Generate automated compliance reports for mental health practices. Track key metrics, audit documentation, and ensure adherence to HIPAA and payer requirements.',
      color: '#a855f7',
      bgColor: 'rgba(168, 85, 247, 0.1)',
      borderColor: 'rgba(168, 85, 247, 0.3)'
    },
    {
      id: 'IC',
      title: 'Insurance Coordination',
      description: 'Automatically coordinate benefits between primary and secondary mental health coverage. Determine correct billing order and ensure maximum reimbursement from all payers.',
      color: '#84cc16',
      bgColor: 'rgba(132, 204, 22, 0.1)',
      borderColor: 'rgba(132, 204, 22, 0.3)'
    }
  ];

  const containerStyle = {
    width: '100%',
    minHeight: '100vh',
    background: '#ffffff',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '0'
  };

  const innerContainerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '4rem 2rem',
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '3rem'
  };

  const titleStyle = {
    fontSize: 'clamp(2rem, 4vw, 2.5rem)',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '1rem',
    lineHeight: '1.2'
  };

  const subtitleStyle = {
    fontSize: '1.125rem',
    color: '#6b7280',
    maxWidth: '900px',
    margin: '0 auto 1.5rem',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '1.5rem',
    marginTop: '2rem'
  };

  const getCardStyle = (index) => ({
    background: 'rgba(248, 250, 252, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '1rem',
    border: `1px solid ${automationProcesses[index].borderColor}`,
    padding: '1.5rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: hoveredCard === index ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
    boxShadow: hoveredCard === index 
      ? '0 20px 40px rgba(0, 0, 0, 0.15)' 
      : '0 10px 25px rgba(0, 0, 0, 0.08)',
    position: 'relative',
    overflow: 'hidden'
  });

  const cardHeaderStyle = (process) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem'
  });

  const iconStyle = (process) => ({
    width: '3rem',
    height: '3rem',
    borderRadius: '0.75rem',
    background: process.bgColor,
    border: `2px solid ${process.borderColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: '700',
    color: process.color,
    flexShrink: 0
  });

  const cardTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  };

  const cardDescriptionStyle = {
    fontSize: '0.95rem',
    color: '#6b7280',
    lineHeight: '1.6',
    margin: 0
  };

  const decorativeElementStyle = (process) => ({
    position: 'absolute',
    top: '-2rem',
    right: '-2rem',
    width: '4rem',
    height: '4rem',
    background: `linear-gradient(135deg, ${process.color}20, ${process.color}10)`,
    borderRadius: '50%',
    opacity: 0.5
  });

  const benefitsStyle = {
    marginTop: '4rem',
    textAlign: 'center',
    padding: '3rem',
    background: 'rgba(248, 250, 252, 0.6)',
    backdropFilter: 'blur(10px)',
    borderRadius: '1.5rem',
    border: '1px solid rgba(226, 232, 240, 0.5)'
  };

  const benefitsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '2rem',
    marginTop: '2rem'
  };

  const benefitItemStyle = {
    textAlign: 'center'
  };

  const benefitNumberStyle = {
    fontSize: '2.5rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '0.5rem'
  };

  const benefitTextStyle = {
    fontSize: '0.95rem',
    color: '#6b7280',
    fontWeight: '500'
  };

  return (
    <div style={containerStyle}>
      <div style={innerContainerStyle}>
        {/* SEO-Optimized Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            Mental Health Revenue Cycle Management Services - Complete Automation
          </h2>
          <p style={subtitleStyle}>
            Streamline your behavioral health practice with our expert <strong>healthcare revenue cycle management services</strong>. 
            As one of the <strong>best revenue cycle management companies</strong>, <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a> provides specialized 
            <strong> medical billing outsourcing services</strong> for mental health providers across the USA.
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#555',
            maxWidth: '850px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Our <strong>medical revenue cycle management services</strong> combine advanced AI automation with proven 
            <strong> healthcare cost reduction strategies</strong>. Learn <strong>how to improve revenue cycle management</strong> 
            for your behavioral health practice with comprehensive <strong>revenue cycle management consulting services</strong> from 
            leading <strong>revenue cycle management services companies</strong>.
          </p>
        </div>

        {/* Automation Process Cards */}
        <div style={gridStyle}>
          {automationProcesses.map((process, index) => (
            <div
              key={process.id}
              style={getCardStyle(index)}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={decorativeElementStyle(process)}></div>
              
              <div style={cardHeaderStyle(process)}>
                <div style={iconStyle(process)}>
                  {process.id}
                </div>
                <h3 style={cardTitleStyle}>{process.title}</h3>
              </div>
              
              <p style={cardDescriptionStyle}>
                {process.description}
              </p>
            </div>
          ))}
        </div>

        {/* Benefits Section with Keywords */}
        <div style={benefitsStyle}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '1rem'
          }}>
            Why Choose MDeRCM for Mental Health Automation?
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#6b7280',
            marginBottom: '2rem',
            maxWidth: '850px',
            margin: '0 auto 2rem auto',
            lineHeight: '1.7'
          }}>
            Transform your mental health practice with our <strong>outsourced revenue cycle management services</strong>. 
            Our <strong>revenue cycle management consulting services</strong> help you learn 
            <strong> how to improve revenue cycle management</strong> with proven <strong>healthcare cost reduction strategies</strong>. 
            Join leading <strong>medical billing outsourcing companies</strong> that trust <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a> for 
            <strong> hospital revenue cycle management</strong> excellence.
          </p>

          <div style={benefitsGridStyle}>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>95%</div>
              <div style={benefitTextStyle}>Auth Approval Rate</div>
            </div>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>70%</div>
              <div style={benefitTextStyle}>Faster Reimbursement</div>
            </div>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>50%</div>
              <div style={benefitTextStyle}>Admin Time Saved</div>
            </div>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>99.7%</div>
              <div style={benefitTextStyle}>Documentation Accuracy</div>
            </div>
          </div>
        </div>

        {/* Additional SEO Keywords Section */}
        <div style={{
          marginTop: '3rem',
          padding: '2.5rem',
          background: 'rgba(243, 244, 246, 0.5)',
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '1.5rem'
          }}>
            Comprehensive Revenue Cycle Management Services for Mental Health Practices
          </h3>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: '#555',
            maxWidth: '900px',
            margin: '0 auto 1.5rem'
          }}>
            Our behavioral health <strong>revenue cycle management services for hospitals</strong> and practices combine 
            <strong> medical billing and coding outsourcing</strong> expertise with AI automation. 
            As specialists among <strong>revenue cycle management services companies</strong>, we deliver 
            <strong> medical revenue cycle management services</strong> that reduce costs and improve collections. 
            Experience proven <strong>healthcare cost reduction consulting</strong> strategies from leading 
            <strong> revenue cycle management services USA</strong> providers.
          </p>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: '#555',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            Discover why mental health providers choose <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a> as their trusted partner for 
            <strong> outsourced revenue cycle management services</strong>. Our comprehensive <strong>healthcare revenue cycle management services</strong> 
            include prior authorization automation, documentation support, claims management, and utilization review—all powered by advanced AI technology.
          </p>
        </div>

        {/* Related Services Links */}
        <div style={{
          marginTop: '3rem',
          padding: '2.5rem',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(99, 102, 241, 0.05))',
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '1.5rem'
          }}>
            Explore Our Other Healthcare Solutions
          </h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            <Link
              to="/healthcare/ai/accounts-receivable"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#2563eb',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Accounts Receivable Management
            </Link>
            <Link
              to="/healthcare/ai/denial-management"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#7c3aed',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Denial Management Services
            </Link>
            <Link
              to="/healthcare/ai/compliance-agent"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#10b981',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Compliance Management
            </Link>
            <Link
              to="/healthcare/ai/payment-posting"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#f97316',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Payment Posting Services
            </Link>
          </div>
          <p style={{
            marginTop: '1.5rem',
            fontSize: '0.95rem',
            color: '#6b7280'
          }}>
            All services powered by <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a>'s advanced AI technology
          </p>
        </div>
      </div>
    </div>
  );
};

export default MENTALAutomationSection;